import { get, isNil, partition, sum } from 'lodash';
import { useCallback } from 'react';

import { ExpandedDamage } from 'components/damages/ExpandedDamage/ExpandedDamage';
import { DamageList2ExpandedRow } from 'components/DamageTable2/ExpandedRow/ExpandedRow';
import type { TDisplayColumnData } from 'components/data/types';
import { getTaskTargetInspectionId } from 'horizon/components/Damages/utils';
import {
  type AtlasGqlArchiveDescendantsCountPayload,
  type AtlasGqlAsset,
  type AtlasGqlGlobalWorkContainerField,
  type AtlasGqlTaskUpdateInput,
  type AtlasGqlWorkCampaignUpdateInput,
  type AtlasGqlWorkContainerFieldValueInput,
  type AtlasGqlWorkOrderCreateInput,
  AtlasGqlWorkContainerFieldType,
} from 'types/atlas-graphql';
import { getFieldValue } from 'utils/useHeaderFields/utils';

export const expandedRowRender = (
  { HORIZON_DAMAGES, LEGACY_DAMAGES }: { HORIZON_DAMAGES: boolean; LEGACY_DAMAGES: boolean },
  task: any
) => {
  if (HORIZON_DAMAGES) {
    let inspectionIdOfGroupToRender: string | undefined;
    if ('targets' in task && task.targets?.length && !!task.horizonDamage) {
      inspectionIdOfGroupToRender = getTaskTargetInspectionId(task.targets);
    }

    return (
      <DamageList2ExpandedRow
        damageId={task?.horizonDamage?.id}
        hideDetails={true}
        taskId={task?.id}
        inspectionIdOfGroupToRender={inspectionIdOfGroupToRender}
      />
    );
  } else if (LEGACY_DAMAGES) {
    return <ExpandedDamage damageId={task?.damage?.id} hideDetails={true} />;
  }
  return null;
};

export const rowExpandableDamages = (
  { HORIZON_DAMAGES, LEGACY_DAMAGES }: { HORIZON_DAMAGES: boolean; LEGACY_DAMAGES: boolean },
  record: any
) => {
  // Mark row as expandable only if we have the appropriate id
  // until Horizon Damages is turned on check on `damage`
  if (HORIZON_DAMAGES) {
    return record?.horizonDamage?.id ? true : false;
  } else if (LEGACY_DAMAGES) {
    return record?.damage?.id ? true : false;
  }
  return false;
};

export function trimAndstripNull(value: string | null | undefined) {
  return value ? value.trim().replace(/\0/g, '') : '';
}

export function useFormatBulkSubmitInput() {
  return useCallback(
    (
      input: AtlasGqlTaskUpdateInput,
      fieldColumns: (TDisplayColumnData | undefined)[],
      workContainerFields: AtlasGqlGlobalWorkContainerField[]
    ) => {
      return formatBulkSubmitInput(input, fieldColumns, workContainerFields);
    },
    []
  );
}

/**
 * Helper function that handles parsing contents from custom field columns into the proper format for bulk updates
 * @param input The standard input object
 * @param fieldColumns The column definitions for the fields configured for the Work Container Type(s) being updated
 * @param workContainerFields The current fields configured for the Work Container Type(s) being updated
 * @returns An input object with all the standard field content of the original input as well as the properly formatted input structure for any custom fields being updated
 */
export const formatBulkSubmitInput = (
  input: AtlasGqlTaskUpdateInput,
  fieldColumns: (TDisplayColumnData | undefined)[],
  workContainerFields: AtlasGqlGlobalWorkContainerField[]
): AtlasGqlTaskUpdateInput => {
  // Grab the keys of all field columns
  const customFieldKeys = fieldColumns.map((col) => col?.key);
  const additionalFields = [];
  const modifiedInput: any = {};
  for (const key in input) {
    // For each field in the bulk update input,
    if (customFieldKeys.includes(key)) {
      const field = workContainerFields.find((f) => f.slug === key);
      const value = get(input, key);
      // Create an object with the field id as the field name and value as the corresponding field value
      const formattedValue = {
        [field?.id]: value,
      };
      additionalFields.push({
        fieldId: field?.id,
        ...getFieldValue(formattedValue, field),
      });
    } else {
      //@ts-ignore - TS doesn't like this, but it is the easiest way to dynamically add a named field to an object
      modifiedInput[key] = get(input, key);
    }
  }
  return { ...modifiedInput, additionalFields };
};

export const getRowKeyWithDamages = (tableData: any[]): string[] => {
  const rowKeysWithDamages = tableData
    .map((task: any) => {
      const taskHasDamage = task?.horizonDamage;
      return taskHasDamage ? task.id : undefined;
    })
    .filter(Boolean);
  return rowKeysWithDamages;
};

/**
 * Helper function aggregates the total number of work containers enumerated in a collection of ArchiveDescendantsCountPayload objects
 * @param counts the objects to aggregate the counts of
 * @returns the total number of work containers enumerated in the counts collection
 */
export const getTotalWorkOrdersCount = (
  counts: AtlasGqlArchiveDescendantsCountPayload[]
): number => {
  const agg: number[] = [];
  counts.forEach((c) => {
    agg.push(c.campaignsCount);
    agg.push(c.tasksCount);
    agg.push(c.workOrdersCount);
  });
  return sum(agg);
};

export const NON_EDITABLE_FIELDS = ['created', 'created_by'];
// Filter out non-editable fields AND the name field, which is editable, but is handled separately than other custom fields
export const FIELDS_TO_FILTER = [...NON_EDITABLE_FIELDS, 'name'];

export type FormValues = Omit<AtlasGqlWorkOrderCreateInput, 'additionalFields'> & {
  [key: string]: any;
};
export const mapFormValuesToWorkOrderInput = (
  values: FormValues,
  workOrderFields: AtlasGqlGlobalWorkContainerField[] = []
): AtlasGqlWorkOrderCreateInput => {
  /**
   * `topLevelFieldKeys` is the set of keys of `AtlasGqlWorkOrderCreateInput`, excluding
   * `additionalFields`. We use this to pull any of those fields out of the form values,
   * then assume the rest of the values are `additionalFields`. Unfortunately, there's
   * not a clean way to do this using the types, so we have to hardcode it instead.
   */
  const topLevelFieldKeys = [
    'collaborators',
    'description',
    'dueDate',
    'hoursActual',
    'hoursEstimated',
    'name',
    'status',
    'vendorOrganizationId',
    'workCampaignId',
  ];

  // Bit of a hack, but rather than create a custom component to handle campaign selection,
  // this just assigns the values from the custom field selector for `parent` to the
  // `workCampaignId` input needed for the CreateWorkOrder mutation
  if (values.parent && !values.workCampaignId) {
    values.workCampaignId = values.parent;
    delete values.parent;
  }

  const [topLevelFields, _additionalFields] = partition(Object.entries(values), ([key]) =>
    topLevelFieldKeys.includes(key)
  );

  const additionalFields: AtlasGqlWorkContainerFieldValueInput[] = _additionalFields
    .map(([key, value]) => {
      if (!isNil(value)) {
        const fieldDefinition = workOrderFields.find(({ slug }) => slug === key);

        if (fieldDefinition) {
          const { id, type } = fieldDefinition;
          return {
            fieldId: id,
            dateValue: type === AtlasGqlWorkContainerFieldType.Date ? value : undefined,
            multiSelectValue:
              type === AtlasGqlWorkContainerFieldType.MultiSelect ? value : undefined,
            numberValue: type === AtlasGqlWorkContainerFieldType.Number ? value : undefined,
            selectValue: type === AtlasGqlWorkContainerFieldType.Select ? value : undefined,
            textValue: type === AtlasGqlWorkContainerFieldType.Text ? value : undefined,
          } as AtlasGqlWorkContainerFieldValueInput;
        }
      }

      return null;
    })
    .filter((f): f is AtlasGqlWorkContainerFieldValueInput => !!f);

  return {
    ...Object.fromEntries(topLevelFields),
    additionalFields,
  };
};

export type WorkCampaignsInputExtendedType = AtlasGqlWorkCampaignUpdateInput & {
  archived?: string;
};

// Delivery maps the inspection onto the For Org's copy of the Turbine, so a Blade in storage cannot
// be delivered; inspection-service refuses it as well.
export const isBladeWithoutTurbine = (
  asset: (Pick<AtlasGqlAsset, 'ancestors'> & { __typename?: string }) | null | undefined
): boolean =>
  asset?.__typename === 'Blade' &&
  !asset.ancestors?.some((ancestor) => ancestor.assetType?.name === 'Turbine');
